import { ListStore } from '@list-store';
import { uniq } from 'lodash';
import {
    Attribute,
    ManageUnitsResource,
    ManageUnitsResponse,
    ManageUnitsUnitGroupEnum,
    ManageUnitsUnitGroupEnumType,
    ManageUnitsUnitView,
    NudgeObject,
    UnitFiltersForm,
    UnitStateGroupEnum,
} from '@mark43/cad-api';
import { transaction } from 'mobx';
import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';
import { numberComparator } from '../common/sorting/comparators';
import { createStreamListener, useStream } from '../socket/use-stream';
import { SelectableUnit, useUnitSelection } from './common/use-unit-selection';
import { AsyncResponsePlanStatus } from './use-async-response-plan';
import { useAsyncResponsePlanContext } from './store/async-response-plan-context';

type ManageUnitsStore = {
    store: ListStore<ManageUnitsUnitView>;
    manageUnitsEventResponse?: ManageUnitsResponse;
    manageUnitsUnitsResponse?: ManageUnitsResponse;
    isEventQueryLoading: boolean;
    isUnitsQueryLoading: boolean;
    isUnitsQueryError: boolean;
    areUnitsFetchedAfterMount: boolean;
    selectedUnitIds: number[];
    releasedUnitIds: number[];
    toggleUnitSelection: (unit: number) => void;
    /**
     * Async plan status, mirrored from the provider context.
     */
    responsePlanStatus: AsyncResponsePlanStatus;
    /**
     * True until the first plan (or first failure) has landed for this panel session, mirrored
     * from the provider context.
     */
    isResponsePlanFirstLoad: boolean;
    /**
     * User-initiated retry of the async plan kick, mirrored from the provider context.
     */
    retryResponsePlan: () => void;
};

const nonSelectedGroups: ManageUnitsUnitGroupEnumType[] = ['AVAILABLE', 'UNAVAILABLE'];

export function useAsyncIntegratedManageUnitsStore({
    eventId,
    defaultFilters,
    selectedFilters,
    unitTypeAttributes,
}: {
    eventId: number;
    defaultFilters: UnitFiltersForm;
    selectedFilters: UnitFiltersForm;
    unitTypeAttributes: Attribute[];
}): ManageUnitsStore {
    const apiClient = useApiClient();

    // The async surface from `<AsyncResponsePlanProvider>`. This file is mounted only on the
    // async-response-plans branch of the host, so the provider is always present here. Read
    // through a ref where a closure (the React Query onSuccess / the nudge tail / the kick
    // effect) must see the latest values without depending on the per-render context object.
    const ctx = useAsyncResponsePlanContext();
    const ctxRef = React.useRef(ctx);
    ctxRef.current = ctx;

    // Group comparator, stable so the initial (neutral) grouping directive and the selection-aware one
    // below share one reference. Identical ordering to the baseline's inline comparator.
    const comparator = useMemo(
        () => (left: ManageUnitsUnitGroupEnumType, right: ManageUnitsUnitGroupEnumType) =>
            numberComparator(
                ManageUnitsUnitGroupEnum[left].index,
                ManageUnitsUnitGroupEnum[right].index
            ),
        []
    );

    const shouldRefetchUnits =
        !!selectedFilters &&
        unitTypeAttributes.length > 0 &&
        (selectedFilters.emsUnitTypeIds.length > 0 ||
            selectedFilters.policeUnitTypeIds.length > 0 ||
            selectedFilters.fireUnitTypeIds.length > 0);
    const store = React.useState(
        () =>
            new ListStore<ManageUnitsUnitView>({
                // Neutral initial grouping (no TO_DISPATCH promotion). Identical to the baseline's
                // directive at creation, where selection is empty (an empty selection promotes nothing).
                // The selection-aware directive is installed by the effect below on the first commit,
                // before any data loads — same reactive update path the baseline already uses.
                groupingDirective: {
                    getGroupValue: (row) => row.manageUnitGroup,
                    comparator,
                },
            })
    )[0];

    // The derived selection hook is a pure function of the visible rows + the push's
    // recommendation set; `recommendedUnitIds` comes from the async push (via ctx), NOT the synced
    // rows, because the units query returns no plan on this path (its rows are AVAILABLE) — the
    // host's upsert effect layers the worker's RECOMMENDED rows in.
    //
    // Reading store.data.list here makes the derived selection reactive to store mutations: the consumer
    // (ManageUnitsDrawer) is a MobX observer, so any setItems/upsertItems/removeItems re-renders this
    // hook and useUnitSelection recomputes from the fresh rows.
    const items: SelectableUnit[] = store.data.list.map((row) => ({
        id: row.id,
        manageUnitGroup: row.manageUnitGroup,
    }));
    const derived = useUnitSelection({ items, recommendedUnitIds: ctx.recommendedUnitIds });

    // Reference-stable (the derived hook stabilizes its arrays via useStableIds), so the consumers
    // below (groupingDirective, queries, nudge helpers) don't churn.
    const selectedUnitIds = derived.selectedUnitIds;
    const toggleUnitSelection = derived.toggle;
    const releasedUnitIds = derived.releasedUnitIds;

    // A unit the user selected can stay in `selectedUnitIds` even after a filter change takes its row
    // out of the CURRENT `items` snapshot — this is intentional, not a leak. It mirrors the sync
    // (legacy) path, which also carries user-selected ids across a filter change unfiltered. The
    // server-side "selected units bypass the filter" rule then always returns a selected unit as a row
    // regardless of filter, so in production the row never actually vanishes.

    // Carry the store's current selection + filters up to the provider's kick. Written every
    // render; the kick reads `.current` at fire-time.
    ctx.selectedUnitIdsRef.current = selectedUnitIds;
    ctx.filtersRef.current = selectedFilters;

    const groupingDirective = useMemo(() => {
        return {
            getGroupValue: (row: ManageUnitsUnitView): ManageUnitsUnitGroupEnumType =>
                nonSelectedGroups.includes(row.manageUnitGroup)
                    ? selectedUnitIds.includes(row.id)
                        ? 'TO_DISPATCH'
                        : row.manageUnitGroup
                    : row.manageUnitGroup,
            comparator,
        };
    }, [selectedUnitIds, comparator]);

    useEffect(() => {
        store.setGroupingDirective(groupingDirective);
    }, [store, groupingDirective]);

    const eventQuery = useQuery({
        queryKey: ['manage-units-event', eventId, defaultFilters] as const,
        queryFn({ queryKey }) {
            return apiClient.request<ManageUnitsResource.GetByEvent>({
                method: 'POST',
                url: 'manage_units/event/',
                data: {
                    ...queryKey[2],
                    eventId: queryKey[1],
                    selectedUnitIds: [],
                    shouldIncludeResponsePlan: false,
                },
            });
        },
    });

    const { refetch: unitsRefetch, ...unitsQuery } = useQuery({
        queryKey: ['manage-units-units', eventId, selectedFilters],
        queryFn() {
            return apiClient.request<ManageUnitsResource.GetByEvent>({
                method: 'POST',
                url: 'manage_units/event/',
                data: {
                    ...selectedFilters,
                    selectedUnitIds,
                    // Opts out of the inline sync plan: the worker computes it off-thread and
                    // delivers it on the responsePlanGenerated stream, avoiding the unbounded sync
                    // closest-unit call.
                    shouldIncludeResponsePlan: false,
                    eventId,
                },
            });
        },
        enabled: shouldRefetchUnits,
        onSuccess(response) {
            if (response.data.success) {
                const data = response.data.data;
                if (data?.units) {
                    store.setItems(data.units);
                }
                const initiallySelectedUnits = data?.initiallySelectedUnits;
                if (initiallySelectedUnits) {
                    store.upsertItems(initiallySelectedUnits);
                }
                // setItems above replaced the store with the (plan-less) units, dropping any
                // RECOMMENDED rows the worker pushed earlier. Re-layer the landed recommended rows
                // whose id is present in the new (filter-scoped) response so a refetch never drops the
                // async recommendation. An old-filter rec absent from the new units is NOT re-layered.
                // Read landed recs via ref so this React Query closure doesn't depend on ctx.
                const newUnitsIds = new Set((data?.units ?? []).map((unit) => unit.id));
                const landedRecommended = ctxRef.current.landedRecommendedUnits.filter((row) =>
                    newUnitsIds.has(row.id)
                );
                if (landedRecommended.length > 0) {
                    store.upsertItems(landedRecommended);
                }
            }
        },
        notifyOnChangeProps: ['data', 'isInitialLoading'],
    });

    const fetchUnitsByIds = React.useMemo(() => {
        return (unitIds: number[]) => {
            return apiClient
                .request<ManageUnitsResource.GetByUnits>({
                    method: 'POST',
                    url: 'manage_units/units/',
                    data: {
                        ...selectedFilters,
                        selectedUnitIds,
                        eventId,
                        unitIds,
                        shouldIncludeResponsePlan: false,
                    },
                })
                .then((response) => {
                    if (response.data.data) {
                        const { units, initiallySelectedUnits } = response.data.data;

                        transaction(() => {
                            const unitIdsInResponse = units.map((unit) => unit.id);
                            const unitIdsWeAskedForThatDidntComeBackInResponse = unitIds.filter(
                                (unitId) => !unitIdsInResponse.includes(unitId)
                            );
                            store.removeItems(unitIdsWeAskedForThatDidntComeBackInResponse);
                            store.upsertItems(units);

                            // /units carries no plan, so a nudged recommended unit comes back
                            // bucketed AVAILABLE and the upsert above just overwrote its RECOMMENDED
                            // row. Re-layer the landed recommended rows for ids present in THIS
                            // response, then surface any server-selected rows not yet in the store;
                            // the derivation then selects them. No reducer reset (derived selection
                            // has no system-selection state to reset).
                            const landedRecommended = ctxRef.current.landedRecommendedUnits.filter(
                                (row) => unitIdsInResponse.includes(row.id)
                            );
                            if (landedRecommended.length > 0) {
                                store.upsertItems(landedRecommended);
                            }

                            const missingInitiallySelectedUnits = initiallySelectedUnits.filter(
                                (initiallySelectedUnit) =>
                                    !store.data.list.find(
                                        (unit) => unit.id === initiallySelectedUnit.id
                                    )
                            );
                            if (missingInitiallySelectedUnits.length) {
                                store.upsertItems(missingInitiallySelectedUnits);
                            }
                        });
                    }
                });
        };
    }, [apiClient, store, eventId, selectedUnitIds, selectedFilters]);

    const getUnitIdsToEvictFromUnitStatusNudges = useMemo(() => {
        return (unitStatusNudges: NudgeObject[] = []): number[] => {
            return unitStatusNudges
                .filter((unitStatusNudge) => {
                    if (unitStatusNudge.u && !store.hasItem(unitStatusNudge.u)) {
                        return false;
                    }
                    if (unitStatusNudge.usg === UnitStateGroupEnum.OFF_DUTY.value) {
                        return true;
                    }
                    // don't evict a selected unit (unless it's logged off)
                    if (unitStatusNudge.u && selectedUnitIds.includes(unitStatusNudge.u)) {
                        return false;
                    }
                    // unit is not in d-area we're filtering for
                    if (selectedFilters.dispatchAreaIds.length > 0 && unitStatusNudge.d) {
                        if (unitStatusNudge.ed) {
                            if (
                                !selectedFilters.dispatchAreaIds.includes(unitStatusNudge.d) &&
                                !selectedFilters.dispatchAreaIds.includes(unitStatusNudge.ed)
                            ) {
                                return true;
                            }
                        } else {
                            if (!selectedFilters.dispatchAreaIds.includes(unitStatusNudge.d)) {
                                return true;
                            }
                        }
                    }
                })
                .map((unitStatusNudge) => unitStatusNudge.u) as number[];
        };
    }, [store, selectedFilters, selectedUnitIds]);

    const getUnitIdsToUpdateFromUnitStatusNudges = React.useMemo(() => {
        return (unitStatusNudges: NudgeObject[] = []): number[] => {
            return unitStatusNudges
                .filter((unitStatusNudge) => {
                    return unitStatusNudge.usg !== UnitStateGroupEnum.OFF_DUTY.value;
                })
                .filter((unitStatusNudge) => {
                    if (unitStatusNudge.u && selectedUnitIds.includes(unitStatusNudge.u)) {
                        return true;
                    }
                    if (selectedFilters.dispatchAreaIds.length > 0 && unitStatusNudge.d) {
                        if (unitStatusNudge.ed) {
                            return (
                                selectedFilters.dispatchAreaIds.includes(unitStatusNudge.d) ||
                                selectedFilters.dispatchAreaIds.includes(unitStatusNudge.ed)
                            );
                        } else {
                            return selectedFilters.dispatchAreaIds.includes(unitStatusNudge.d);
                        }
                    } else {
                        return true;
                    }
                })
                .map((unitStatusNudge) => unitStatusNudge.u) as number[];
        };
    }, [selectedFilters.dispatchAreaIds, selectedUnitIds]);

    const getUnitIdsInStoreFromUnitNudges = React.useMemo(() => {
        return (unitNudges: NudgeObject[] = []): number[] => {
            return unitNudges
                .map((unitNudge) => {
                    return unitNudge.id;
                })
                .filter((unitId) => !!unitId && store.hasItem(unitId));
        };
    }, [store]);

    const getUnitIdsInStoreFromNonUnitNudges = React.useMemo(() => {
        return (nonUnitNudges: NudgeObject[] = []): number[] => {
            return nonUnitNudges
                .map((nonUnitNudge) => {
                    return nonUnitNudge.u;
                })
                .filter((unitId) => !!unitId && store.hasItem(unitId)) as number[];
        };
    }, [store]);

    const listener = useMemo(() => {
        return createStreamListener({
            streamNames: ['nudge'],
            onMessage: (message) => {
                const unitIdsToEvict = getUnitIdsToEvictFromUnitStatusNudges(
                    message.payload.map['USS']
                );
                if (unitIdsToEvict.length > 0) {
                    store.removeItems(unitIdsToEvict);
                }
                const unitIdsToUpdate = [
                    ...getUnitIdsToUpdateFromUnitStatusNudges(message.payload.map['USS']),
                    ...getUnitIdsInStoreFromUnitNudges(message.payload.map['U']),
                    ...getUnitIdsInStoreFromNonUnitNudges(message.payload.map['UUL']),
                    ...getUnitIdsInStoreFromNonUnitNudges(message.payload.map['ULI']),
                ];
                if (unitIdsToUpdate.length > 0) {
                    fetchUnitsByIds(uniq(unitIdsToUpdate));
                    // A nudge burst may have changed unit availability. Debounced re-kick with the
                    // current filters. Read via ref so the listener doesn't depend on the per-render
                    // ctx object.
                    ctxRef.current.rekick();
                }
            },
            onSocketStatusChange: (socketStatusChangeEvent) => {
                if (
                    socketStatusChangeEvent.status === 'CONNECTED' &&
                    socketStatusChangeEvent.timesConnected > 1
                ) {
                    unitsRefetch();
                    // The async re-kick on reconnect is owned by useAsyncResponsePlan's socket listener.
                    // Re-kicking here too would double-fire when a reconnect resolves a deferred first
                    // load. Units refetch on reconnect on both paths.
                }
            },
        });
    }, [
        getUnitIdsToUpdateFromUnitStatusNudges,
        getUnitIdsInStoreFromUnitNudges,
        getUnitIdsInStoreFromNonUnitNudges,
        getUnitIdsToEvictFromUnitStatusNudges,
        fetchUnitsByIds,
        store,
        unitsRefetch,
    ]);

    useStream(listener);

    const areUnitsFetchedAfterMount = unitsQuery.isFetchedAfterMount;
    // One-way latch of the above. isFetchedAfterMount toggles back to false while a filter-change refetch
    // is in flight. The recommended-row upsert effect must not re-fire on that toggle: it would upsert a
    // stale landed payload. This latch turns true on the first settle and stays true, so the effect
    // re-fires only on a real new push.
    const [haveUnitsSettledOnce, setHaveUnitsSettledOnce] = React.useState(false);
    useEffect(
        function latchUnitsSettledOnce() {
            if (areUnitsFetchedAfterMount && !haveUnitsSettledOnce) {
                setHaveUnitsSettledOnce(true);
            }
        },
        [areUnitsFetchedAfterMount, haveUnitsSettledOnce]
    );

    // Kick wiring. The kick fires the first time the unit filters resolve (shouldRefetchUnits flips
    // true), not at mount: at mount the filters are unknown, and an empty-filter kick would bucket
    // recommendations outside the visible list. The first transition defers until the socket is
    // connected (requestInitialKick); every later selectedFilters change uses the debounced rekick
    // instead. The kick functions are read from ctxRef, so they are not effect dependencies.
    const hasKickedRef = React.useRef(false);
    useEffect(
        function kickAsyncResponsePlanOnFilterTransition() {
            if (!shouldRefetchUnits) {
                return;
            }
            if (!hasKickedRef.current) {
                hasKickedRef.current = true;
                ctxRef.current.requestInitialKick();
            } else {
                ctxRef.current.rekick();
            }
        },
        [shouldRefetchUnits, selectedFilters]
    );

    // Reconcile the store's RECOMMENDED rows against the landed push. unitsQuery returns NO plan,
    // so its rows are AVAILABLE; setting recommendedUnitIds alone would NOT system-select them
    // (useUnitSelection only selects RECOMMENDED/ALREADY_DISPATCHED/HELD rows). Upserting the
    // worker's RECOMMENDED-grouped views makes those rows carry the RECOMMENDED group so the
    // derivation picks them up. This effect covers the ordering where a fresh push lands while
    // units are ALREADY settled (no refetch follows, so onSuccess does not re-run); the onSuccess
    // re-layer above covers the other ordering. Gated on the one-way latch (not live
    // isFetchedAfterMount): the latch turns true on the first settle and stays true, so a
    // filter-change refetch that briefly flips isFetchedAfterMount back to false cannot re-fire
    // this effect with a stale landed payload.
    //
    // Reconcile = upsert AND demote. A row a PREVIOUS push promoted that the new payload no longer
    // contains must fall back to AVAILABLE: nudges only refetch the nudged ids and the /units
    // re-layer re-asserts the landed payload, so without demotion a superseded recommendation
    // stays RECOMMENDED (and mis-sorted, mis-counted) until the next full setItems — the "release a
    // unit elsewhere and the old recs stick in the Recommended section" bug. Selection needs no
    // demotion help: the derived selection is already gated on the CURRENT push's
    // recommendedUnitIds.
    const landedRecommendedUnits = ctx.landedRecommendedUnits;
    useEffect(
        function reconcileLandedRecommendedUnits() {
            if (!haveUnitsSettledOnce) {
                return;
            }
            const landedUnitIds = new Set(landedRecommendedUnits.map((unit) => unit.id));
            const staleRecommendedRows = store.data.list
                .filter(
                    (row) => row.manageUnitGroup === 'RECOMMENDED' && !landedUnitIds.has(row.id)
                )
                .map((row) => ({ ...row, manageUnitGroup: 'AVAILABLE' as const }));
            // Mirror the onSuccess re-layer (above): only re-assert a landed recommendation whose id
            // is present in the current filter-scoped rows. Without this, a recommendation computed
            // for an old filter set gets injected into the current (different-filter) view.
            const currentRowIds = new Set(store.data.list.map((row) => row.id));
            const inScopeLandedRecommended = landedRecommendedUnits.filter((unit) =>
                currentRowIds.has(unit.id)
            );
            transaction(() => {
                if (staleRecommendedRows.length > 0) {
                    store.upsertItems(staleRecommendedRows);
                }
                if (inScopeLandedRecommended.length > 0) {
                    store.upsertItems(inScopeLandedRecommended);
                }
            });
        },
        [haveUnitsSettledOnce, landedRecommendedUnits, store]
    );

    // Merge the landed push's plan fields onto the exposed units response so banners,
    // primary-unit selection, the header, and the auto-open gate — all of which read
    // manageUnitsUnitsResponse.{responsePlanV2, initialPrimaryUnitId, initiallySelectedUnits} —
    // work unchanged. Keyed on the plan fields (reference-stable from the hook) so
    // manageUnitsUnitsResponse stays reference-stable between pushes (guards against a Maximum
    // update depth error).
    const baseUnitsResponse = unitsQuery.data?.data.data;
    const manageUnitsUnitsResponse = useMemo<ManageUnitsResponse | undefined>(() => {
        if (!baseUnitsResponse) {
            return baseUnitsResponse;
        }
        // Until the plan LANDS, the async context's initiallySelectedUnits / initialPrimaryUnitId are
        // EMPTY (idle/pending) or stay empty on a first-load failure — but the base /event response
        // already carries the event's already-dispatched units + existing primary (server-computed,
        // plan-independent). Fall back to the base values during that window so the "Already Dispatched"
        // pill row and the existing primary aren't blanked while the push is in flight (~1-40s); on a
        // failed first load the base values persist (the sync path minus recommendations). Once landed,
        // the worker's payload is a SUPERSET (already-dispatched ∪ recommended), so the context values
        // take over wholesale — do NOT union. responsePlanV2 stays ctx-driven (the client sends
        // shouldIncludeResponsePlan:false, so base responsePlanV2 is null anyway).
        const planLanded = ctx.status === 'landed';
        return {
            ...baseUnitsResponse,
            responsePlanV2: ctx.responsePlanV2,
            initialPrimaryUnitId: planLanded
                ? ctx.initialPrimaryUnitId
                : baseUnitsResponse.initialPrimaryUnitId,
            initiallySelectedUnits: planLanded
                ? ctx.initiallySelectedUnits
                : baseUnitsResponse.initiallySelectedUnits,
        };
    }, [
        baseUnitsResponse,
        ctx.status,
        ctx.responsePlanV2,
        ctx.initialPrimaryUnitId,
        ctx.initiallySelectedUnits,
    ]);

    return {
        store,
        manageUnitsEventResponse: eventQuery.data?.data.data,
        manageUnitsUnitsResponse,
        isEventQueryLoading: eventQuery.isInitialLoading,
        isUnitsQueryLoading: unitsQuery.isInitialLoading,
        isUnitsQueryError: unitsQuery.isError,
        selectedUnitIds,
        toggleUnitSelection,
        releasedUnitIds,
        areUnitsFetchedAfterMount,
        // Async response-plan UI state surfaced for the drawer, from the provider context.
        responsePlanStatus: ctx.status,
        isResponsePlanFirstLoad: ctx.isFirstLoad,
        retryResponsePlan: ctx.retry,
    };
}
